// Note: createSyncStoragePersister is marked deprecated but still fully functional
// The experimental replacement API is not yet stable, so we continue using this
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import { QueryClient } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import React from "react"
import ReactDOM from "react-dom/client"
import { I18nextProvider } from "react-i18next"
import { BrowserRouter } from "react-router-dom"
import App from "./App"
import i18n from "./i18n"

import "./index.css"
// Validate environment variables at startup
import "./env"
import { initSentry } from "./sentry"
import * as Sentry from "@sentry/react"

initSentry()

const loadThemeStyles = async () => {
  try {
    if (import.meta.env.VITE_THEME === "gea") {
      await import("./styles/themeGea.css")
    } else if (import.meta.env.VITE_THEME === "gecontur") {
      await import("./styles/themeGecontur.css")
    } else if (import.meta.env.VITE_THEME === "geabrasil") {
      await import("./styles/themeGeaBrasil.css")
    }
  } catch (error) {
    console.error("Error loading theme styles:", error)
  }
}

loadThemeStyles()
// Set page title from env if provided
if (import.meta.env.VITE_TITLE) {
  document.title = import.meta.env.VITE_TITLE
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement)

// Configure QueryClient with optimized defaults for persistence
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep data in cache
      staleTime: 1000 * 60 * 5, // 5 minutes - data is fresh for 5 min
      retry: 1, // Only retry once on failure
      refetchOnWindowFocus: true, // Revalidate when user returns to tab
      refetchOnReconnect: true, // Revalidate when connection restored
    },
  },
})

// Create persister using localStorage
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "GECONTUR_REACT_QUERY_CACHE", // Unique key for this app
})

root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
        dehydrateOptions: {
          // Only persist specific queries (user, offers, headers)
          shouldDehydrateQuery: (query) => {
            const queryKey = query.queryKey[0] as string
            return ["user", "offers", "headers"].includes(queryKey)
          },
        },
      }}
    >
      <I18nextProvider i18n={i18n}>
        <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>} showDialog>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </Sentry.ErrorBoundary>
      </I18nextProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  </React.StrictMode>,
)
